import React from "react";
import { useDashboard } from "@/hooks/useResource";
import { KpiCard } from "@/components/common/KpiCard";
import { PageHeader } from "@/components/common/PageHeader";
import { RevenueArea, ChannelBars, Funnel, DonutChart } from "@/components/common/Charts";
import { ProgressRing } from "@/components/common/ProgressRing";
import { Skeletons } from "@/components/common/EmptyState";
import { ChartLineUp, Trophy, Star } from "@phosphor-icons/react";

export default function Dashboard() {
  const { data, isLoading } = useDashboard();

  if (isLoading || !data) {
    return (
      <div>
        <PageHeader title="Marketing Dashboard" subtitle="Your command centre across every brand" icon={ChartLineUp} />
        <Skeletons count={8} />
      </div>
    );
  }

  const b = data.budget;
  const spentPct = b.approved ? Math.round((b.actual / b.approved) * 100) : 0;

  return (
    <div>
      <PageHeader title="Marketing Dashboard" subtitle="Your command centre across every brand" icon={ChartLineUp} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.kpis.map((k, i) => <KpiCard key={k.label} {...k} delay={i * 0.04} />)}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-surface-raised p-6 lg:col-span-2">
          <h2 className="mb-4 font-display text-lg font-bold">Revenue vs Spend</h2>
          <RevenueArea data={data.trend} />
        </div>
        <div className="rounded-2xl border border-border bg-surface-raised p-6">
          <h2 className="mb-2 font-display text-lg font-bold">Budget Utilisation</h2>
          <div className="flex items-center gap-5 py-3">
            <ProgressRing value={spentPct} size={96} stroke={9} color="#7C3AED" />
            <div className="space-y-1 text-sm">
              <p className="flex justify-between gap-6"><span className="text-muted-foreground">Approved</span><span className="font-mono">₹{(b.approved / 100000).toFixed(1)}L</span></p>
              <p className="flex justify-between gap-6"><span className="text-muted-foreground">Committed</span><span className="font-mono">₹{(b.committed / 100000).toFixed(1)}L</span></p>
              <p className="flex justify-between gap-6"><span className="text-muted-foreground">Actual</span><span className="font-mono">₹{(b.actual / 100000).toFixed(1)}L</span></p>
              <p className="flex justify-between gap-6"><span className="text-muted-foreground">Remaining</span><span className="font-mono text-lime-400">₹{(b.remaining / 100000).toFixed(1)}L</span></p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-surface-raised p-6">
          <h2 className="mb-4 font-display text-lg font-bold">Conversion Funnel</h2>
          <Funnel data={data.funnel} />
        </div>
        <div className="rounded-2xl border border-border bg-surface-raised p-6">
          <h2 className="mb-4 font-display text-lg font-bold">Revenue by Channel</h2>
          <ChannelBars data={data.channel_revenue} />
        </div>
        <div className="rounded-2xl border border-border bg-surface-raised p-6">
          <h2 className="mb-4 font-display text-lg font-bold">Channel Mix</h2>
          <DonutChart data={data.channel_revenue} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="rounded-2xl border border-border bg-gradient-to-br from-brand-violet/15 to-surface-raised p-6">
          <div className="mb-3 flex items-center gap-2 text-brand-yellow"><Trophy size={20} weight="fill" /><span className="text-sm font-bold uppercase tracking-wider">Best Campaign</span></div>
          <p className="font-display text-xl font-black">{data.best_campaign?.name}</p>
          <p className="mt-1 text-sm text-muted-foreground">{data.best_campaign?.brand} · ROAS {data.best_campaign?.roas}x · ₹{((data.best_campaign?.actual_revenue || 0) / 100000).toFixed(1)}L revenue</p>
        </div>
        <div className="rounded-2xl border border-border bg-gradient-to-br from-brand-pink/15 to-surface-raised p-6">
          <div className="mb-3 flex items-center gap-2 text-brand-pink"><Star size={20} weight="fill" /><span className="text-sm font-bold uppercase tracking-wider">Top Influencer</span></div>
          <p className="font-display text-xl font-black">{data.best_influencer?.name}</p>
          <p className="mt-1 text-sm text-muted-foreground">{data.best_influencer?.followers?.toLocaleString()} followers · ROI {data.best_influencer?.roi}x · ₹{((data.best_influencer?.revenue_generated || 0) / 1000).toFixed(0)}K</p>
        </div>
      </div>
    </div>
  );
}
